import type { ScenarioInput } from '@exigo/shared';
import { Card } from '../../components/ui/Card';
import { Select } from '../../components/ui/Select';
import { NumberInput } from '../../components/ui/TextInput';
import type { ReferenceData } from '../../api/types';
import styles from './CalculatorPage.module.css';

interface DealContextCardProps {
  input: ScenarioInput;
  setInput: (updater: (prev: ScenarioInput) => ScenarioInput) => void;
  reference: ReferenceData;
}

const MARKET_OPTIONS = [
  { value: 'A', label: 'A — Australia, NZ, UK, US, EU' },
  { value: 'B', label: 'B — all other eligible' },
  { value: 'C', label: 'C — where a third rate applies' },
];

const TERM_OPTIONS = [
  { value: '12', label: '12 months' },
  { value: '24', label: '24 months' },
  { value: '36', label: '36 months' },
  { value: '60', label: '60 months' },
];

/** Segment, market, FX and term — the deal-level context every engagement reads. */
export function DealContextCard({ input, setInput, reference }: DealContextCardProps): React.JSX.Element {
  const segmentOptions = reference.segments.map((s) => ({ value: s, label: s }));

  return (
    <Card className={styles.section}>
      <p className={styles.sectionTitle}>Deal context</p>
      <div className={styles.grid}>
        <div className={styles.field}>
          {/* A caption, not a <label>: Select is a Radix trigger button, not a
              native form control, and names itself via `ariaLabel` below — a
              <label htmlFor> here would reference an id that does not exist. */}
          <span className={styles.fieldLabel}>Customer segment</span>
          <Select
            value={input.segment}
            onChange={(v) => { setInput((p) => ({ ...p, segment: v as ScenarioInput['segment'] })); }}
            options={segmentOptions}
            ariaLabel="Customer segment"
          />
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>Payment market</span>
          <Select
            value={input.market}
            onChange={(v) => { setInput((p) => ({ ...p, market: v as ScenarioInput['market'] })); }}
            options={MARKET_OPTIONS}
            ariaLabel="Payment market"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="fx">
            AUD per 1 USD
          </label>
          <NumberInput
            id="fx"
            value={input.fx > 0 ? input.fx / 1_000_000 : 0}
            onValueChange={(v) => { setInput((p) => ({ ...p, fx: Math.round(v * 1_000_000) })); }}
            step={0.01}
            min={0}
            placeholder="e.g. 1.53"
            ariaLabel="AUD per 1 USD"
          />
          <span className={styles.fieldHint}>
            Required for AUD. Every band boundary in the guide is USD — set today&rsquo;s rate rather
            than trusting a stale one.
          </span>
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>Contract term</span>
          <Select
            value={String(input.termMonths)}
            onChange={(v) => {
              setInput((p) => ({ ...p, termMonths: Number(v) as ScenarioInput['termMonths'] }));
            }}
            options={TERM_OPTIONS}
            ariaLabel="Contract term"
          />
          <span className={styles.fieldHint}>MCI still earns on year 1 only.</span>
        </div>
      </div>
    </Card>
  );
}
